import {
  useEffect, useId, useRef, type ButtonHTMLAttributes, type InputHTMLAttributes,
  type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes,
} from 'react'
import { cn } from '@/lib/cn'

export { NumberField } from './NumberField'

// ---------- Button ----------

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md'

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-gold-500 text-on-accent hover:bg-gold-400 font-semibold shadow-[0_2px_12px_-4px] shadow-gold-500/40',
  secondary: 'bg-ink-750/80 text-ink-100 hover:bg-ink-700 border border-line',
  ghost: 'text-ink-400 hover:text-ink-100 hover:bg-fill',
  danger: 'bg-blood-500/12 text-blood-500 hover:bg-blood-500 hover:text-white border border-blood-500/30',
}

const SIZES: Record<Size, string> = {
  sm: 'h-8 px-3.5 text-[13px] rounded-[10px] gap-1.5',
  md: 'h-10 px-4.5 text-sm rounded-xl gap-2',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  busy?: boolean
}

export function Button({ variant = 'secondary', size = 'md', busy, className, children, disabled, ...rest }: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || busy}
      className={cn(
        'pressable inline-flex items-center justify-center whitespace-nowrap font-medium',
        'disabled:opacity-40 disabled:pointer-events-none',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
    >
      {busy && <Spinner className="size-3.5" />}
      {children}
    </button>
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn('inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent', className)}
    />
  )
}

// ---------- Surfaces ----------

export function Panel({ className, children, ...rest }: { className?: string; children: ReactNode } & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div {...rest} className={cn('panel panel-lit', className)}>
      {children}
    </div>
  )
}

export function SectionHeading({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-400">{children}</h2>
      {action}
    </div>
  )
}

const TONES = {
  gold: 'bg-gold-500/12 text-gold-400 border-gold-500/20',
  arcane: 'bg-arcane-500/12 text-arcane-400 border-arcane-500/20',
  moss: 'bg-moss-500/12 text-moss-500 border-moss-500/20',
  blood: 'bg-blood-500/12 text-blood-500 border-blood-500/20',
  neutral: 'bg-fill text-ink-300 border-line',
} as const

export function Badge({ tone = 'neutral', children, className }: { tone?: keyof typeof TONES; children: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium tracking-wide', TONES[tone], className)}>
      {children}
    </span>
  )
}

// ---------- Form fields ----------

const CONTROL = 'w-full rounded-xl border border-line bg-ink-900/60 px-3.5 py-2.5 text-sm text-ink-100 placeholder:text-ink-500 transition-colors hover:border-line-strong focus:border-gold-500/60 focus:bg-ink-900 focus:outline-none disabled:opacity-40'

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: (id: string) => ReactNode }) {
  const id = useId()
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-[11px] font-medium uppercase tracking-[0.08em] text-ink-400">
        {label}
      </label>
      {children(id)}
      {error ? (
        <p className="text-xs text-blood-500">{error}</p>
      ) : hint ? (
        <p className="text-xs text-ink-500">{hint}</p>
      ) : null}
    </div>
  )
}

export function Input({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...rest} className={cn(CONTROL, className)} />
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...rest} className={cn(CONTROL, 'min-h-24 resize-y leading-relaxed', className)} />
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className={cn(CONTROL, 'appearance-none bg-[length:14px] bg-[right_0.75rem_center] bg-no-repeat pr-9', className)}
      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 8' fill='none' stroke='%238792a5' stroke-width='1.6'%3E%3Cpath d='M1 1.5 6 6.5 11 1.5'/%3E%3C/svg%3E\")" }}
    >
      {children}
    </select>
  )
}

// ---------- Feedback ----------

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="rounded-xl border border-blood-500/25 bg-blood-500/10 px-3.5 py-2.5 text-sm text-blood-500">
      {children}
    </p>
  )
}

export function EmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[--radius-card] border border-dashed border-line px-6 py-14 text-center">
      <h3 className="font-display text-lg text-ink-300">{title}</h3>
      <p className="max-w-sm text-sm text-ink-500">{description}</p>
      {action}
    </div>
  )
}

export function PageLoader() {
  return (
    <div className="flex items-center justify-center py-24 text-ink-500">
      <Spinner className="size-6" />
    </div>
  )
}

// ---------- Modal ----------

export function Modal({ open, title, onClose, children }: { open: boolean; title: string; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null)

  // Drive the native <dialog> so we inherit focus trapping and Esc for free.
  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return
    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  return (
    <dialog
      ref={ref}
      onCancel={(event) => { event.preventDefault(); onClose() }}
      onClick={(event) => { if (event.target === ref.current) onClose() }}
      className="material panel-lit m-auto w-[min(32rem,calc(100vw-2rem))] rounded-[--radius-card] p-0 text-ink-100 backdrop:bg-[--color-scrim] backdrop:backdrop-blur-md"
    >
      <div className="flex items-center justify-between border-b border-line px-5 py-4">
        <h2 className="font-display text-base font-semibold">{title}</h2>
        <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">✕</Button>
      </div>
      <div className="p-5">{children}</div>
    </dialog>
  )
}
